import { useContext, useEffect } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import Image from 'next/image'
import { s3Static } from '../../utils/constants'
import { BlogHeaderLinks } from '../../components/blogs/BlogHeaderLinks'
import { contentDate } from '../../utils/article-date-time'
import { BlogsList } from '../../components/blogs/BlogList'
import { blogsMaster } from '../../utils/sg-blogs-data'
import { LocationContext } from '../../context/LocationContext'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);
    const blogs = blogsMaster['finance-insurance-investment'].blogArray;

  return {
      props: {
        modifiedDate,
        month,
        year,
        blogs
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
  }
}

export default function Home(props) {
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const blogs = props.blogs

    const { countryCd } = useContext(LocationContext)

  return (
    <>
      <Head>
        <title>Importance of various types of Insurance</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="Explore the different type of insurance that you might need to help you financially during tough times."/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/blogs/types-of-insurance" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Importance of various types of Insurance`}/>
        <meta property="og:description" content="Explore the different type of insurance that you might need to help you financially during tough times." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-28T07:30:46+00:00" />
        <meta property="article:modified_time" content="2025-01-04T07:30:46+00:00" />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/financial-consultant-medium.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/financial-consultant-medium.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/blogs/types-of-insurance"/>
        <meta name="twitter:title" content={`Importance of various types of Insurance`}/> 
        <meta name="twitter:description" content="Explore the different type of insurance that you might need to help you financially during tough times."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/blogs/types-of-insurance" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/blogs/types-of-insurance"},"headline":"Importance of various types of Insurance","description":"Explore the different type of insurance that you might need to help you financially during tough times.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/financial-consultant-medium.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-28T07:30:46+00:00","dateModified":"2022-09-04T07:30:46+00:00"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Blogs","item":"https://www.stringssg.com/blogs"},{"@type":"ListItem","position":1,"name":"10-ways-to-save-money"}]}]}`
          }}
        />
      </Head>
      <BlogHeaderLinks/>
      <div className='max-w-5xl mx-auto py-5 px-2'>
            <div className='space-y-2'>
                <h1 className='text-xl text-gray-500 font-semibold'>Types of insurance your need!</h1>
                <div className='text-gray-400 text-xs'>
                    <time dateTime="2022-08-28 00:00">August 28th 2022</time>
                </div>
            </div>
            <div className='py-2 text-gray-500 leading-8 space-y-2'>
                <h2 className='font-semibold'>
                    What does the term &quot;Insurance&quot; mean?
                </h2>
                <p className=''>
                Insurance is like a financial guard or protection against any possible event or a range of events. It is an arrangement or coverage  by which the insurance company or insurer provides a guarantee of compensation for some specified loss, death of breadwinner, some kind of permanent disability, property damage, critical illness, or in return for payment of a specified premium. In any of such above mentioned occurrences, the insurance company or insurer from whom we bought our policy will have to pay us an agreed amount to cover some of our losses. It is the work of an Insurance Agent or Financial Adviser to look after our policy from the beginning and guide and help us throughout the period when we hold the policy to him.
                </p>
                <div className='relative h-96 max-w-xl'>
                    <Image className='rounded-lg'
                            src={s3Static + "financial-consultant-medium.jpg"}
                            fill
                            style={{objectFit: 'cover'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='insurance types'
                    />          
                </div>
                <h2 className='font-semibold'>
                    Why do we need Insurance?
                </h2>
                <p className=''>
                We all work for a good life and to fulfill our dreams and live for our loved ones. Naturally, because insurance is all about providing financial safety to ourselves and our family, protecting business from financial loss or liabilities etc it is such an important component of our financial planning. That is why we need to insure our life and our assets. This helps us to enjoy financial security, reduce stress during hard times and also secures the financial future for our family members after the death of the breadwinner.
                </p>
                <div className='py-2 space-y-2'>
                    <h2 className='font-bold'>
                        Types of Insurance:
                    </h2>
                    <ul className='list-decimal space-y-2 p-6'>
                        <li>
                        <strong>Life Insurance</strong> - Life insurance also known as life cover or life assurance is a policy or a contract between the policy holder and an insurer or where the former chooses the amount of coverage, time period and pays his premiums either monthly or annually. In return, his family has the reassurance from the insurer to pay the designated beneficiary a sum of money upon the death of an insured person when a valid claim is made. With this money the grieving family will get some financial help like they can pay the household bills, take care of the child-care costs or pay any debt if there were any etc.
                        </li>
                        <li>
                        <strong>Health Insurance</strong> - An unexpected event like critical illness, disability, trauma or accident, may leave us with some financial loss. Health insurance covers the risk of a person incurring medical expenses in whole or in part. It covers the cost of the insured&apos;s medical and surgical expenses. However, depending on the type of health insurance coverage the person have he will either have to pay the costs out of his pocket and later he will be reimbursed or the insurer makes payments directly to the respective authority i.e hospital etc.
                        </li>
                        <li>
                        <strong>General Insurance</strong> - General insurance is the insurance that covers tangible assets that are susceptible to damages like fire, marine, damages to car, accident, damage to house. Hence it is called as non-life insurance policies too. General insurance products are bought as they provide protection against unforeseeable contingencies. In this type of insurance too, products come at a price in the form of premium. If any of the above mentioned events happens, then the insurance company have to pay the client an agreed amount to cover some or all of the losses. In such cases, it is the duty of the Insurance Agent or Financial Adviser to help and guide us throughout the process.
                        </li>
                        <li>
                        <strong>Vehicle Insurance</strong> - A vehicle is often expensive and we all want to protect it. Vehicle insurance is insurance for different types of road vehicles like - our cars, bikes, trucks, motorcycles. This gives financial protection against physical damage after an accident or damages due to traffic collisions etc. Hence depending on what coverage we select, vehicle insurance will be paying to repair for our vehicle.
                        </li>
                        <li>
                        <strong>Critical illness insurance</strong> - This is an insurance to cover expenses of any critical illness. As per the contract the Insurance Company has to make a lump sum cash payment if the policyholder is diagnosed with one of the specific illnesses as mentioned in the predetermined list of policy papers.  Such illness includes paralysis, stroke, severe burns, heart attack, any surgical procedure, cancer, coma etc.
                        </li>
                        <li>
                        <strong>Home Insurance</strong> - This insurance is also called homeowner&apos;s insurance. It gives coverage to the losses and damages of the policy holder&apos;s house and the assets in his home. It is a type of property insurance that covers a private residence. Such damages include which are caused by fires, theft, explosion, lightning strikes, household apparatus etc. However, all natural disasters are not covered by home insurance like damage caused by earthquakes and floods mostly does not fall under the coverage.
                        </li>
                        <li>
                        <strong>Travel Insurance</strong> - Today many people feels it safer to be covered while travelling. We can opt for the travel insurance at the time of the booking of a trip to cover us during the duration of that trip. Travel Insurance usually covers trip cancellation cost, cost of lost, stolen or damaged baggage, medical expenses, delayed baggage, flight accidents etc.
                        </li>
                        <li>
                        <strong>Pet Insurance</strong> - Insurance for our pets is a form of property insurance. Under this category, veterinary treatment, accidents, illnesses, injuries, rehabilitation, surgery or if the pet is lost or stolen are mostly covered.
                        </li>
                    </ul>
                </div>
                
                <div className='py-2 space-y-2'>
                    <p>
                        Let us understand why we need an Insurance Agent or Financial Adviser. They are the best person to rely on as they have proper knowledge of the market. They are like our personal advisers who understand our individual needs and accordingly make sure that we are adequately covered.  Since they are licensed insurance professionals their advice can give us the maximum protection which we might miss out if we search on our own.
                    </p>
                </div>
                <div className='py-2 text-center'>
                    <Link href={`/${countryCd}/financial-adviser-singapore`} prefetch={false} className='px-3 py-3 border border-amber-600 text-orange-600 rounded-lg'>
                            Get a Financial Adviser
                    </Link>
                </div>
            </div>
            <BlogsList blogs={blogs}/>
        </div>
        <div className='fixed bottom-0 w-full text-center text-lg'>
            <Link href={`/${countryCd}/financial-adviser-singapore`} prefetch={false} className='text-white py-3 bg-linear-to-tr from-amber-600 to-amber-400 block font-bold w-full'>
                    Get a Financial Adviser
            </Link>
        </div>
      <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
